import { ServiceItem, ServiceTransaction } from '../types';
import { undatedBacklog } from './invoiceLogic';
import { waivedQuantity } from './projectLv';
import { isAccountingEntry, isBackfilledAltEntry, isFlatProofEntry, loggedQuantity } from './serviceLogUtils';

/**
 * RUECKNAHME einer Leistungsbuchung — die Regeln.
 *
 * Das Gegenstueck zum VERZICHT (serviceWaiverLogic), und der Unterschied ist
 * der ganze Punkt:
 *
 *   Verzicht   = die Leistung wurde erbracht und wird nicht mehr gefordert.
 *                `quantityUsed` BLEIBT.
 *   Ruecknahme = die Buchung haette es nie geben duerfen. Die Begehung ist
 *                ausgefallen, der Bericht war eine Dublette, oder ein Zaehler
 *                hat Menge erfunden. `quantityUsed` SINKT.
 *
 * Wer eine ausgefallene Begehung "wegverzichtet", traegt sie fuer immer als
 * erbracht im Leistungsnachweis. Wer eine erbrachte Begehung zuruecknimmt,
 * loescht sie aus dem Protokoll, obwohl sie stattgefunden hat. Deshalb sind es
 * zwei Vorgaenge mit zwei Knoepfen und nicht einer mit einem Schalter.
 *
 * ZWEI GESTALTEN, die dieselbe Ursache haben koennen:
 *
 *   a) Es gibt einen Protokolleintrag zu viel (Dublette, ausgefallene
 *      Begehung, die trotzdem gemeldet wurde). Er wird ENTFERNT und
 *      `quantityUsed` um seine Menge gesenkt.
 *
 *   b) Es gibt gar keinen Eintrag — `quantityUsed` wurde direkt hochgesetzt.
 *      Das ist die Protokolluecke aus `undatedBacklog`, und genau so sah der
 *      Zaehlerfehler aus, der auf mehreren Projekten eine Begehung erfunden
 *      hat (Projekt 5954: erledigt 32 bei 31 Protokolleintraegen). Dort gibt
 *      es nichts zu loeschen, nur `quantityUsed` zu senken.
 *
 * Beide Wege hinterlassen dieselbe Spur: einen Eintrag mit Praefix `RN-` und
 * Menge 0. Menge 0, weil sonst die Summe der Bewegungen nicht mehr zum
 * Leistungsstand passte — der entfernte Eintrag ist ja weg, und die Luecke
 * wurde geschlossen, indem der Stand selbst gesenkt wurde.
 *
 * Sonderfall NACHWEISZEILE der Monatspauschale (`NW-…`, Menge 0): sie wird
 * wie a) entfernt und hinterlaesst dieselbe Spur, aber `quantityUsed` sinkt
 * NICHT — sie hat nie etwas gezaehlt (Monate zaehlen allein ueber
 * `FLAT-JJJJ-MM`).
 *
 * Pure Funktionen, kein Supabase, kein Date.now().
 */

/** Praefix der Protokollspur einer Ruecknahme. Siehe serviceLogUtils.isAccountingEntry. */
export const REVERSAL_REFERENCE_PREFIX = 'RN-';

const MIN_REASON_LENGTH = 3;

const round3 = (n: number): number => Math.round(n * 1000) / 1000;

/**
 * Die Protokolleintraege einer Position, die als Leistung zaehlen und damit
 * ueberhaupt zurueckgenommen werden koennen.
 *
 * Buchungsvorgaenge (`BU-`, `VZ-`, `RN-`) sind KEINE Leistung und stehen
 * deshalb nicht zur Wahl — sie zurueckzunehmen hiesse, eine Abrechnung
 * rueckgaengig zu machen, und dafuer gibt es das Storno im Rechnungsmodul.
 * Eintraege ohne Menge ebenfalls nicht: dort gaebe es nichts zu senken.
 *
 * Ausnahme mit `{ nachweise: true }`: die NACHWEISZEILE auf der Pauschale
 * (`NW-…`, Menge 0, siehe `isReversibleProofEntry`). Nur der Dialog und die
 * Planung der Ruecknahme fragen so. Ohne die Option bleibt der Begriff
 * „Leistung mit Menge" — auf ihm stehen die Doppelbuchungs- und
 * Tageszwillingsbefunde der Integritaetspruefung, und ein Nachweis-Zwilling
 * ist dort keine Geldforderung (ihr Satz „… EUR netto zu viel" waere falsch).
 */
export const reversibleEntries = (
    service: Pick<ServiceItem, 'id'>,
    log: ServiceTransaction[],
    opts?: { nachweise?: boolean },
): ServiceTransaction[] =>
    (log || []).filter(
        (t) =>
            t?.serviceId === service.id &&
            !isAccountingEntry(t) &&
            ((t.quantity || 0) > 0 || (opts?.nachweise === true && isReversibleProofEntry(t))),
    );

/**
 * Eine NACHWEISZEILE, die die Ruecknahme anbietet: Referenz `NW-…` und keine
 * Menge.
 *
 * Ohne diesen Weg blieb ein falscher oder doppelter Nachweis fuer immer
 * stehen (Opus-Review 30.09., P2): er zaehlt als Begehung (Kachel, Rhythmus,
 * Cockpit), und ueber den Ref-Riegel (`same_report`) sperrt er die richtige
 * Buchung desselben Berichts.
 *
 * Monatsbuchungen (`FLAT-JJJJ-MM`) und Buchungsvorgaenge fallen schon an der
 * Referenz heraus — sie beginnen nie mit `NW-`. Eine `NW-`-Zeile MIT Menge
 * (Handaenderung am Blob) ist hier ausdruecklich KEIN Nachweis: sie traegt
 * eine Menge, die der Zaehler kennt, und geht den gewoehnlichen Weg (senken,
 * Schranken).
 */
export const isReversibleProofEntry = (
    t: Pick<ServiceTransaction, 'bookingReference' | 'quantity'> | null | undefined,
): boolean =>
    isFlatProofEntry(t) && Math.abs(Number(t?.quantity) || 0) < 0.0005;

/**
 * Die bisherigen Ruecknahmen dieser Position — die 'RN-'-Spuren, juengste zuerst.
 *
 * Der Dialog zeigt sie, damit sichtbar ist, was schon weg ist: eine Spur mit
 * Menge 0 taucht in keiner Leistungsliste auf, und die verbleibenden Zwillinge
 * eines mehrfach gebuchten Berichts sehen aus wie vorher.
 */
export const reversalTraces = (
    service: Pick<ServiceItem, 'id'>,
    log: ServiceTransaction[],
): ServiceTransaction[] =>
    (log || [])
        .filter(
            (t) =>
                t?.serviceId === service.id &&
                typeof t.bookingReference === 'string' &&
                t.bookingReference.startsWith(REVERSAL_REFERENCE_PREFIX),
        )
        .sort((a, b) => String(b.bookedAt || b.date || '').localeCompare(String(a.bookedAt || a.date || '')));

/** Ein zur Wahl stehender Eintrag mit dem, was der Dialog ueber ihn sagen muss. */
export interface AnnotatedReversibleEntry {
    entry: ServiceTransaction;
    /**
     * Wie viele Eintraege dieser Position denselben Bericht buchen — 1 heisst
     * „einmalig", 2 oder 3 heisst „Zwilling". Ohne `reportRef` immer 1: ohne
     * Bericht laesst sich Gleichheit nicht behaupten.
     */
    duplicateCount: number;
    /**
     * Wie oft zu DIESEM Bericht bereits zurueckgenommen wurde. Steht hier 1 und
     * `duplicateCount` ist 1, ist das der verbleibende, richtige Eintrag.
     */
    priorReversals: number;
    /**
     * Was von DIESEM Eintrag bereits teilweise zurueckgenommen wurde (Summe
     * der `reversedQuantity` seiner Spuren). 0 heisst: der Eintrag traegt noch
     * seine urspruengliche Menge. Der Dialog zeigt daraus „ursprünglich 4".
     */
    partiallyReversed: number;
    /**
     * Eine Nachweiszeile der Pauschale (`isReversibleProofEntry`): keine Menge,
     * keine Teilruecknahme, der Leistungsstand bleibt. Der Dialog zeigt
     * „Nachweis" statt einer Menge.
     */
    nachweis: boolean;
}

/**
 * Die waehlbaren Eintraege mit Zwillings- und Ruecknahme-Kennzeichnung.
 *
 * Zwillinge werden ueber den BERICHT erkannt (`reportRef` gleich), nicht ueber
 * Datum oder Text: Datum und Text sind bei jeder zweiten Begehung gleich, der
 * Bericht ist es nur bei einer Doppelbuchung. Reihenfolge bleibt die des
 * Protokolls.
 */
export const annotateReversibleEntries = (
    service: Pick<ServiceItem, 'id'>,
    log: ServiceTransaction[],
): AnnotatedReversibleEntry[] => {
    const entries = reversibleEntries(service, log, { nachweise: true });
    const jeBericht = new Map<string, number>();
    for (const e of entries) {
        const ref = (e.reportRef || '').trim();
        if (ref) jeBericht.set(ref, (jeBericht.get(ref) || 0) + 1);
    }
    const zurueck = new Map<string, number>();
    const teilweise = new Map<string, number>();
    for (const t of reversalTraces(service, log)) {
        const ref = (t.reversedReportRef || '').trim();
        const id = String(t.reversedEntryId ?? '').trim();
        const menge = Number(t.reversedQuantity);
        const teil = Boolean(id) && Number.isFinite(menge) && menge > 0;
        if (teil) {
            // Eine TEILWEISE Ruecknahme hat den Eintrag stehen lassen — sie
            // zaehlt nicht als „zu diesem Bericht wurde schon zurueckgenommen":
            // der Satz ist fuer entfernte Zwillinge geschrieben und hiesse
            // hier faelschlich, ein Eintrag sei weg.
            teilweise.set(id, round3((teilweise.get(id) || 0) + menge));
        } else if (ref) {
            zurueck.set(ref, (zurueck.get(ref) || 0) + 1);
        }
    }
    return entries.map((entry) => {
        const ref = (entry.reportRef || '').trim();
        return {
            entry,
            duplicateCount: ref ? jeBericht.get(ref) || 1 : 1,
            priorReversals: ref ? zurueck.get(ref) || 0 : 0,
            partiallyReversed: teilweise.get(String(entry.id ?? '').trim()) || 0,
            nachweis: isReversibleProofEntry(entry),
        };
    });
};

/**
 * Die Protokolluecke dieser Position: als erbracht gefuehrte Menge, von der
 * kein Eintrag erzaehlt.
 *
 * Bewusst ueber `undatedBacklog` aus invoiceLogic und nicht selbst gerechnet.
 * Dieselbe Zahl an zwei Stellen unabhaengig zu bilden ist im Repo schon
 * mehrfach auseinandergelaufen, und diese hier entscheidet ueber Geld.
 */
export const protocolGap = (service: ServiceItem, log: ServiceTransaction[]): number =>
    round3(undatedBacklog([service], log || []).find((i) => i.serviceId === service.id)?.quantity ?? 0);

/**
 * Was zurueckgenommen werden soll: ein konkreter Eintrag oder ein Teil der Luecke.
 *
 * `quantity` am Eintrag ist die TEILMENGE: fehlt sie oder deckt sie die ganze
 * Menge des Eintrags, wird der Eintrag entfernt wie bisher; ist sie kleiner,
 * bleibt der Eintrag stehen und traegt nur noch den Rest (4 Fahrten an einem
 * Tag gebucht, 3 davon nie gefahren → 1 bleibt).
 */
export type ReversalTarget =
    | { kind: 'entry'; entryId: string; quantity?: number }
    | { kind: 'gap'; quantity: number };

export interface ReversalInput {
    target: ReversalTarget;
    reason: string;
    actorName: string;
    actorId?: string;
    now: string;
    /** Kennung des Spur-Eintrags, hereingereicht. */
    traceId: string;
    /** Buchungsreferenz der Spur, hereingereicht. */
    reference: string;
    /**
     * Was von dem gewaehlten Eintrag bereits AUSGESTELLTEN Rechnungen zugeordnet
     * ist (`allocations.entryId`, Storno-Gegenbuchungen verrechnet) — vom
     * Schreibpfad geladen, hier nur geprueft. Unter diese Menge darf der
     * Eintrag nicht gekuerzt und schon gar nicht entfernt werden: der Beleg
     * zeigte sonst auf mehr Leistung, als der Eintrag noch traegt, und
     * `allocationLogic.openEntries` verwirft den negativen Rest still.
     *
     * Die Schranke ueber `quantityBilled` faengt das nicht: bei A = 4, B = 4,
     * 3 auf A zugeordnet ist A → 1 ueber den Gesamtstand erlaubt (5 ≥ 3).
     * Fehlt der Wert (Luecke, reine Logikpruefung), gilt 0.
     */
    allocatedQuantity?: number;
}

export interface ReversalPlan {
    /** Die Position mit gesenktem Leistungsstand. */
    service: ServiceItem;
    /** Das VOLLSTAENDIGE neue Protokoll — der falsche Eintrag ist daraus entfernt. */
    log: ServiceTransaction[];
    /**
     * Die ids der Protokollzeilen, die dieser Plan aus dem Log ENTFERNT.
     *
     * Sie stehen hier, weil nur diese Funktion sie kennt: wer sie spaeter aus
     * einem Vergleich von altem und neuem Log zurueckrechnet, bildet dieselbe
     * Entscheidung ein zweites Mal — und die Wege, auf denen im Repo zwei
     * unabhaengig gebildete Mengen auseinandergelaufen sind, sind zu zaehlreich.
     * Der Schreibpfad braucht sie fuer die Zweitschrift: das Journal kennt kein
     * Delete, die entfernte Zeile muss dort ausdruecklich zurueckgenommen
     * werden (`voidJournalEntries`).
     *
     * Bei `target.kind === 'gap'` und bei einer TEILWEISEN Ruecknahme wird
     * nichts entfernt — dann leer.
     */
    removedEntryIds: string[];
    /**
     * Die Zeile, die dieser Plan im Log KUERZT statt entfernt — die teilweise
     * Ruecknahme. Sie behaelt ihre id (Rechnungszuordnungen zeigen darauf) und
     * traegt nur noch `to`.
     *
     * Der Schreibpfad braucht sie, weil das Journal weder Update noch Delete
     * kennt: die gespiegelte Zeile mit `from` wird stillgelegt und die
     * gekuerzte Zeile neu angehaengt (unter abgeleitetem Schluessel mit
     * `legacy_entry_id`, weil die id im Journal schon vergeben ist).
     */
    reducedEntry?: { id: string; from: number; to: number };
    /** Die zurueckgenommene Menge (die Zeile, die aus dem Protokoll geht, oder der gekuerzte Teil). */
    quantity: number;
    /**
     * Um wie viel der Leistungsstand wirklich sinkt. Kleiner als `quantity`,
     * wenn der Zaehler die entfernte Zeile nie (ganz) gezaehlt hatte — der
     * Zwillingsfall 6051 #135. Dann bleibt er, und die Spur sagt es.
     */
    usedDelta: number;
    /**
     * Die entfernte Zeile war eine Nachweiszeile der Pauschale (Menge 0).
     * `service` ist dann die Position UNVERAENDERT: es gab keine Menge, also
     * sinkt nichts. Der Schreibpfad gibt deshalb auch keinen Zaehler an die
     * RPC — die Pauschale wird nicht angefasst.
     */
    nachweis: boolean;
    noteText: string;
}

/** Flach, nicht unterschieden — siehe die Begruendung in serviceWaiverLogic.WaiverResult. */
export interface ReversalResult {
    ok: boolean;
    plan?: ReversalPlan;
    error?: string;
}

/**
 * Eine Ruecknahme planen. Schreibt nichts, entscheidet nur.
 */
export const planReversal = (
    service: ServiceItem,
    log: ServiceTransaction[],
    input: ReversalInput,
): ReversalResult => {
    const reason = (input.reason || '').trim();
    if (reason.length < MIN_REASON_LENGTH) {
        return { ok: false, error: 'Bitte eine Begründung angeben — sie steht später im Protokoll.' };
    }

    const used = service.quantityUsed || 0;
    const billed = service.quantityBilled || 0;
    const waived = waivedQuantity(service);

    let quantity: number;
    /** Der Eintrag, um den es geht — entfernt oder gekuerzt. */
    let ziel: ServiceTransaction | undefined;
    /** Nur bei einer TEILWEISEN Ruecknahme: was der Eintrag vorher und nachher traegt. */
    let reduced: { id: string; from: number; to: number } | undefined;
    /** Der Eintrag ist eine Nachweiszeile der Pauschale — ohne Menge. */
    let nachweis = false;
    let newLog = [...(log || [])];

    if (input.target.kind === 'entry') {
        ziel = reversibleEntries(service, log, { nachweise: true }).find((t) => t.id === input.target['entryId']);
        if (!ziel) {
            return { ok: false, error: 'Der Protokolleintrag wurde nicht gefunden oder ist keine Leistungsbuchung.' };
        }
        // DIESELBE id ZWEIMAL IM PROTOKOLL — die Ruecknahme wird abgewiesen
        // (Review 23.09.2026, P2-1/P2-6). Sie waere in BEIDEN Gestalten nicht
        // eindeutig:
        //   • ganz:     die Entfernung geht ueber die id und naehme BEIDE
        //               Zeilen aus dem Protokoll, waehrend der Zaehler nur um
        //               EINE Menge sinkt — Protokoll und Leistungsstand
        //               widersprechen einander danach dauerhaft.
        //   • teilweise: der Server lehnt die Kuerzung bei einer Dublette
        //               ausdruecklich ab (`v_treffer > 1` in
        //               service_log_remove_entries, Nr. 118) — der Vorgang
        //               brach also ohnehin ab, nur eine Netzrunde spaeter und
        //               mit einer Meldung aus der Datenbank.
        // Erst die Dublette klaeren (Quarantaeneklasse `doppelt_im_projekt`),
        // dann zuruecknehmen.
        if ((log || []).filter((t) => String(t?.id ?? '') === String(ziel!.id)).length > 1) {
            return {
                ok: false,
                error: 'Diese Protokollzeile steht mit derselben id mehrfach im Protokoll (Quarantäneklasse doppelt_im_projekt). Bitte die Dublette zuerst klären — eine Rücknahme wäre hier nicht eindeutig.',
            };
        }
        nachweis = isReversibleProofEntry(ziel);
        const ganz = round3(ziel.quantity || 0);
        const teil = input.target.quantity;
        // NACHWEISZEILE: keine Menge, also auch keine Teilmenge — die Zeile
        // geht ganz (Menge 0, unten der Zweig „GANZER Eintrag"). Eine
        // mitgegebene Menge wird nicht ausgelegt: es gibt nichts, wovon sie
        // ein Teil sein koennte.
        const gewuenscht = nachweis || teil === undefined || teil === null ? ganz : round3(Number(teil));
        if (!nachweis && (!Number.isFinite(gewuenscht) || gewuenscht <= 0)) {
            return { ok: false, error: 'Bitte eine Menge größer als 0 angeben.' };
        }
        if (gewuenscht > ganz + 0.0005) {
            return {
                ok: false,
                error: `Der Protokolleintrag trägt nur ${ganz} ${service.unit || ''}.`.replace('  ', ' '),
            };
        }
        if (gewuenscht >= ganz - 0.0005) {
            // Der GANZE Eintrag: er verschwindet aus dem Protokoll.
            quantity = ganz;
            newLog = newLog.filter((t) => t.id !== ziel!.id);
        } else {
            // TEILWEISE (Betreiber 22.09.2026: „4 Fahrten an einem Tag gebucht,
            // nur 3 stornieren"). Der Eintrag bleibt unter seiner id stehen und
            // traegt nur noch den Rest — die id darf nicht wechseln, weil
            // `allocations.entryId` ausgestellter Rechnungen darauf zeigt, und
            // eine Minuszeile daneben (Muster der Korrekturzeile aus dem
            // Nachtrag-Dialog) stuende im Leistungsnachweis als eigene Zeile.
            // Die urspruengliche Menge steht in der Spur (`reversedQuantity`)
            // und in der stillgelegten Journalzeile.
            quantity = gewuenscht;
            const rest = round3(ganz - gewuenscht);
            reduced = { id: String(ziel.id), from: ganz, to: rest };
            // Ueber die REFERENZ, nicht ueber die id. Die Dublette ist oben
            // abgewiesen, dieser Vergleich kann sie also nicht mehr treffen —
            // er bleibt trotzdem, weil er die Absicht festhaelt: gekuerzt wird
            // die GEWAEHLTE Zeile und nicht „jede mit dieser id".
            newLog = newLog.map((t) => (t === ziel ? { ...t, quantity: rest } : t));
        }
    } else {
        const gap = protocolGap(service, log);
        quantity = round3(input.target.quantity);
        if (!Number.isFinite(quantity) || quantity <= 0) {
            return { ok: false, error: 'Bitte eine Menge größer als 0 angeben.' };
        }
        if (gap <= 0) {
            return {
                ok: false,
                error: 'Zu dieser Position erzählt das Protokoll von jeder erbrachten Menge. Es gibt keine ungedeckte Menge zurückzunehmen — nimm den betreffenden Eintrag zurück.',
            };
        }
        if (quantity > gap) {
            return { ok: false, error: `Ohne Protokolleintrag stehen nur ${gap} ${service.unit || ''} da.`.replace('  ', ' ') };
        }
    }

    // SCHRANKE JE EINTRAG: was ein ausgestellter Beleg diesem Eintrag
    // zugeordnet hat, muss im Eintrag stehen bleiben (Begruendung an
    // `ReversalInput.allocatedQuantity`).
    const zugeordnet = round3(Math.max(0, Number(input.allocatedQuantity) || 0));
    if (ziel && zugeordnet > 0.0005) {
        const bleibt = reduced ? reduced.to : 0;
        if (bleibt < zugeordnet - 0.0005) {
            const ganz = round3(ziel.quantity || 0);
            const frei = round3(Math.max(0, ganz - zugeordnet));
            const einheit = service.unit || '';
            return {
                ok: false,
                error: (frei <= 0.0005
                    ? `Dieser Protokolleintrag ist vollständig abgerechnet (${zugeordnet} ${einheit} in ausgestellten Rechnungen zugeordnet). Er lässt sich nur über ein Storno im Rechnungsmodul zurücknehmen.`
                    : `Von diesem Protokolleintrag sind bereits ${zugeordnet} ${einheit} ausgestellten Rechnungen zugeordnet. Höchstens ${frei} ${einheit} lassen sich zurücknehmen — die abgerechnete Menge nur über ein Storno im Rechnungsmodul.`
                ).replace(/  /g, ' '),
            };
        }
    }

    // Der Zaehler sinkt nur um das, was er NACH der Entfernung noch ueber dem
    // Protokoll stuende — nie um mehr.
    //
    // Vorfall 6051 #135 (02.09.2026): der Versand schrieb dieselbe Berichtszeile
    // zweimal (300 ms auseinander, beide Laeufe vom selben Ausgangsstand), der
    // Zaehler stieg dabei nur EINMAL. Die Ruecknahme des Zwillings senkte ihn
    // trotzdem um 1 — danach zaehlte er #135 gar nicht mehr, und die Rechnung
    // haette eine Begehung zu wenig gefordert (Waechter: protokolliert 98,
    // erledigt 97). Derselbe Riegel steht seit Nr. 78 auf dem Server
    // (project_services_counter_guard); hier steht er, damit der Blob, das
    // Journal und der Hinweis an den Bearbeiter dieselbe Zahl tragen. Bei
    // `gap` ist die Luecke per Definition ueber dem Protokoll, dort greift die
    // Schranke nie.
    //
    // ALT-Zeilen zaehlen dabei nur, soweit sie Leistung TRAGEN, die das eigene
    // Protokoll nicht erklaert — dieselbe Regel wie performedUpToCutoff
    // (invoiceLogic, 6290-1). Vorfall 5972 (21.10.2026): 21 Berichte einzeln im
    // Protokoll, dazu der Abrechnungsvermerk „ALT-5972/3 (12 Begehung)". Die
    // Ruecknahme der Maerzbegehung rechnete 20 + 12 = 32 > 21, meldete „der
    // Zaehler hatte diese Zeile nie gezaehlt" und liess erbracht auf 21 — die
    // Rechnung zum Stichtag forderte die zurueckgenommene Begehung weiter, weil
    // die ALT-Zeile die Differenz 21 − 20 trug. Bei einer nachgetragenen
    // Altposition (ALT IST die Leistung, erbracht = eigene + ALT) und bei der
    // Luecke (sie liegt ueber eigene + ALT) ergibt die Regel dasselbe wie die
    // rohe Summe.
    const eigeneVorher = loggedQuantity((log || []).filter((t) => !isBackfilledAltEntry(t)), service.id);
    const eigeneNachher = loggedQuantity(newLog.filter((t) => !isBackfilledAltEntry(t)), service.id);
    const altMenge = Math.max(0, loggedQuantity(newLog.filter((t) => isBackfilledAltEntry(t)), service.id));
    const altTraegt = Math.min(altMenge, Math.max(0, used - eigeneVorher));
    const loggedAfter = round3(eigeneNachher + altTraegt);
    const usedDelta = round3(Math.min(quantity, Math.max(0, used - loggedAfter)));
    const newUsed = round3(used - usedDelta);

    // HARTE SCHRANKE: unter das bereits Abgerechnete darf der Leistungsstand
    // nicht fallen. Sonst forderte ein ausgestellter Beleg mehr, als das
    // Projekt als erbracht kennt — Beleg und Projekt widersprechen einander,
    // und der Beleg ist festgeschrieben. Der Weg dafuer ist das Storno im
    // Rechnungsmodul, nicht diese Maske.
    //
    // Beide Schranken (diese und der Verzicht darunter) schuetzen eine MENGE.
    // Eine Nachweiszeile traegt keine und senkt nichts — sie greifen dort
    // nicht. Sonst sperrte ein ohnehin schiefer Stand der Pauschale (erledigt
    // unter abgerechnet) oder ein Verzicht auf einen Monat die Entfernung
    // eines falschen Nachweises, obwohl beide davon unberuehrt bleiben.
    if (!nachweis && newUsed < billed - 0.0005) {
        return {
            ok: false,
            error: `Davon sind bereits ${billed} ${service.unit || ''} abgerechnet. Eine abgerechnete Menge lässt sich nur über ein Storno im Rechnungsmodul zurücknehmen.`.replace(
                '  ',
                ' ',
            ),
        };
    }

    // Steht ein Verzicht auf der Position, muss er zuerst weg: er bezieht sich
    // auf eine Menge, die es nach der Ruecknahme so nicht mehr gibt. Stillweg
    // mitzukuerzen waere eine zweite, unsichtbare Entscheidung.
    if (!nachweis && waived > 0 && newUsed < billed + waived - 0.0005) {
        return {
            ok: false,
            error: `Auf ${waived} ${service.unit || ''} dieser Position wurde bereits verzichtet. Hebe den Verzicht auf, bevor du die Buchung zurücknimmst.`.replace(
                '  ',
                ' ',
            ),
        };
    }

    const einheit = service.unit || '';
    const woher = ziel
        ? (reduced
            ? `Protokolleintrag vom ${(ziel.date || '').slice(0, 10)}, Rest ${reduced.to} ${einheit}`.replace('  ', ' ')
            : `Protokolleintrag vom ${(ziel.date || '').slice(0, 10)}`)
        : 'Menge ohne Protokolleintrag';
    // Bei der teilweisen Ruecknahme steht die ganze Menge daneben („3 von 4"):
    // die Spur ist spaeter der einzige Ort im Protokoll, der sie noch nennt.
    const mengeText = nachweis
        ? 'Nachweis'
        : reduced ? `${quantity} von ${reduced.from} ${einheit}` : `${quantity} ${einheit}`;
    // Was mit dem Zaehler geschieht, steht in der Spur UND in der Notiz: wer
    // spaeter „98 → 98" liest, soll nicht raten muessen, warum.
    const zaehlerSatz = nachweis
        ? `Leistungsstand bleibt ${used} (eine Nachweiszeile trägt keine Menge)`
        : usedDelta < quantity - 0.0005
            ? (usedDelta <= 0.0005
                ? `Leistungsstand bleibt ${used} (der Zähler hatte diese Zeile nie gezählt)`
                : `Leistungsstand ${used} → ${newUsed} (der Zähler hatte nur ${usedDelta} ${einheit} davon gezählt)`.replace('  ', ' '))
            : `Leistungsstand ${used} → ${newUsed}`;

    const trace: ServiceTransaction = {
        id: input.traceId,
        date: input.now,
        serviceId: service.id,
        // Menge 0: der falsche Eintrag ist entfernt bzw. der Stand direkt
        // gesenkt. Eine negative Menge hier zoege ein zweites Mal ab.
        quantity: 0,
        performerId: input.actorId || '',
        performerName: input.actorName,
        isSubstitution: false,
        description: `Buchung zurückgenommen: ${mengeText} (${woher}) — ${reason}`.replace('  ', ' ')
            + (nachweis || usedDelta < quantity - 0.0005 ? ` — ${zaehlerSatz}` : ''),
        bookedAt: input.now,
        bookedBy: input.actorName,
        bookedById: input.actorId,
        bookingReference: input.reference,
        // WELCHE Buchung weg (oder gekuerzt) ist — sonst sieht bei Zwillingen
        // der verbleibende Eintrag genauso aus wie der entfernte (siehe
        // ServiceTransaction).
        ...(ziel ? { reversedEntryId: String(ziel.id) } : {}),
        ...(ziel?.reportRef ? { reversedReportRef: ziel.reportRef } : {}),
        ...(reduced ? { reversedQuantity: quantity } : {}),
    };

    return {
        ok: true,
        plan: {
            // Nachweis: die Position unveraendert — quantityUsed und status
            // der Pauschale fasst dieser Vorgang nicht an (Vertrag Nachweis).
            service: nachweis ? service : { ...service, quantityUsed: newUsed },
            log: [trace, ...newLog],
            removedEntryIds: ziel && !reduced ? [String(ziel.id)] : [],
            ...(reduced ? { reducedEntry: reduced } : {}),
            quantity,
            usedDelta,
            nachweis,
            noteText:
                `Buchung zurückgenommen: ${mengeText} `.replace('  ', ' ') +
                `„${service.title}" (${service.lvPosition || 'ohne LV-Nr.'}), ${woher}. ` +
                `${zaehlerSatz}. Grund: ${reason}`,
        },
    };
};
